import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import '@fontsource-variable/inter';
import '@fontsource-variable/outfit';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import './styles/globals.css';
import './styles/content-theme.css';

import { BASE_PATH } from './lib/base-path';
import { BRAND_NAME, CONTENT_ONLY } from './lib/ui-mode';
import { routeTree } from './routeTree.gen';
import { queryClient } from './lib/query-client';
import { setAuthFailureHandler } from './lib/api-client';
import { initTheme } from './lib/theme';

initTheme();

export const router = createRouter({
  routeTree,
  basepath: BASE_PATH || '/',
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Route the "session is gone" transition through the router rather than a hard reload.
setAuthFailureHandler(() => {
  // The router's own location, which excludes the mount path: comparing the
  // browser's pathname would never match '/login' when mounted under a subpath.
  const { pathname, searchStr } = router.state.location;
  if (pathname.startsWith('/login')) return;
  void router.navigate({ to: '/login', search: { redirect: `${pathname}${searchStr}` } });
});

if (CONTENT_ONLY) {
  document.title = `${BRAND_NAME} · Content`;
  // Switches on the soft content theme; every rule in it is scoped to this attribute.
  document.documentElement.dataset.ui = 'content';
}

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
